import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, PenLine, BadgeCheck, Percent, Euro, Users2, Clock, Gauge, MapPin, Radio, User } from 'lucide-react';
import { useFilters } from '@/context/FilterContext';
import { usePost } from '@/lib/useData';
import { comparisonInfo } from '@/lib/periods';
import { PageHeader } from '@/components/common/PageHeader';
import { SectionCard } from '@/components/common/SectionCard';
import { KpiCard } from '@/components/kpi/KpiCard';
import { TrendComparisonNote } from '@/components/common/TrendComparisonNote';
import { GridSkeleton } from '@/components/common/Loading';
import { EmptyState } from '@/components/common/EmptyState';
import { TrendAreaChart } from '@/components/charts/TrendAreaChart';
import { AgeDonut } from '@/components/charts/AgeDonut';
import { ScoreGauge } from '@/components/common/ScoreGauge';
import { StatusPill } from '@/components/common/StatusPill';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { fmtInt, fmtPct, fmtEur, fmtNum } from '@/lib/format';

const spark = (series, key) => (series || []).map((s) => ({ x: s.period, y: s[key] }));

export default function CampagneDetail() {
  const { code } = useParams();
  const navigate = useNavigate();
  const { body, periodKey, period, comparePeriod } = useFilters();
  const { data, isLoading } = usePost(`/analytics/campaign/${encodeURIComponent(code)}`, body);
  const k = data?.kpis;
  const meta = data?.meta || {};
  const cmp = comparisonInfo(periodKey, period, comparePeriod);

  return (
    <div className="space-y-6">
      <Button variant="ghost" size="sm" className="gap-1.5" onClick={() => navigate('/campagnes')} data-testid="back-campaigns">
        <ArrowLeft className="h-4 w-4" /> Campagnes
      </Button>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-mono text-2xl font-semibold accent-text">{code}</h1>
          <div className="mt-2 flex flex-wrap gap-2 text-xs">
            {meta.region && <Badge variant="secondary" className="gap-1 rounded-full"><MapPin className="h-3 w-3" />{meta.region}</Badge>}
            {meta.canal && <Badge variant="secondary" className="gap-1 rounded-full"><Radio className="h-3 w-3" />{meta.canal}</Badge>}
            {meta.responsable && <Badge variant="secondary" className="gap-1 rounded-full"><User className="h-3 w-3" />{meta.responsable}</Badge>}
          </div>
        </div>
        {data?.objective && <StatusPill status={data.objective.status} />}
      </div>

      {isLoading ? <GridSkeleton count={8} /> : !data || data.record_count === 0 ? <EmptyState /> : (
        <>
          <div>
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-sm font-semibold text-foreground">Indicateurs clés</h2>
              <TrendComparisonNote />
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
              <KpiCard label="BS signés" value={fmtInt(k.bs_rue.value)} icon={PenLine} trend={k.bs_rue.pct_change} trendTooltip={cmp.full} spark={spark(data.series_day, 'bs_rue')} />
              <KpiCard label="BS validés" value={fmtInt(k.bs_reel.value)} icon={BadgeCheck} trend={k.bs_reel.pct_change} trendTooltip={cmp.full} spark={spark(data.series_day, 'bs_reel')} accent />
              <KpiCard label="Taux de transformation" value={fmtPct(k.taux_transfo.value)} icon={Percent} trend={k.taux_transfo.pct_change} trendTooltip={cmp.full} />
              <KpiCard label="Don moyen" value={fmtEur(k.don_moyen.value)} icon={Euro} trend={k.don_moyen.pct_change} trendTooltip={cmp.full} />
              <KpiCard label="Âge moyen" value={`${fmtNum(k.age_moyen.value, 1)} ans`} icon={Users2} trend={k.age_moyen.pct_change} trendTooltip={cmp.full} />
              <KpiCard label="Heures réalisées" value={fmtInt(k.heures.value)} icon={Clock} trend={k.heures.pct_change} trendTooltip={cmp.full} />
              <KpiCard label="Productivité horaire" value={`${fmtNum(k.productivite.value, 2)} BS/h`} icon={Gauge} trend={k.productivite.pct_change} trendTooltip={cmp.full} />
              <KpiCard label="Montant (mensuel)" value={fmtEur(k.montant.value, 0)} icon={Euro} trend={k.montant.pct_change} trendTooltip={cmp.full} />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
            <SectionCard title="Évolution hebdomadaire" subtitle="BS signés vs validés" className="xl:col-span-2">
              {data.series_week?.length ? (
                <TrendAreaChart data={data.series_week} formatTip={(v) => fmtInt(v)} formatY={(v) => fmtInt(v)}
                  series={[{ key: 'bs_rue', name: 'BS signés', color: '#5B6472' }, { key: 'bs_reel', name: 'BS validés', color: 'var(--tenant-accent-500)' }]} />
              ) : <EmptyState />}
            </SectionCard>
            <SectionCard title="Score" subtitle="Performance du code">
              {data.score && <div className="flex justify-center py-2"><ScoreGauge score={data.score.score} rating={data.score.rating} /></div>}
            </SectionCard>
          </div>

          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            <SectionCard title="Répartition par âge">
              {data.age_distribution?.some((a) => a.count > 0) ? <AgeDonut data={data.age_distribution} /> : <EmptyState body="Aucune donnée d'âge." />}
            </SectionCard>
            <SectionCard title="Transformation & productivité" subtitle="Tendance hebdomadaire">
              {data.series_week?.length ? (
                <TrendAreaChart data={data.series_week} formatTip={(v, n) => n === 'Taux transfo' ? fmtPct(v) : fmtNum(v, 2)}
                  series={[{ key: 'taux_transfo', name: 'Taux transfo', color: 'var(--tenant-accent-500)' }, { key: 'productivite', name: 'Productivité', color: '#E9B949' }]} />
              ) : <EmptyState />}
            </SectionCard>
          </div>
        </>
      )}
    </div>
  );
}
